import { useCallback, useEffect, useRef, useState } from "react";
import { getMyActivationEntitlement } from "@/features/billing/api/entitlementsApi";
import { getMyPendingCashPayment, requestCashPayment, verifyCashPayment, cashErrorMessage } from "@/features/billing/api/cashPaymentApi";
import { friendlyDataError } from "@/lib/errors";

async function resolvePhase() {
  if (await getMyActivationEntitlement()) return { phase: "activated", tx: null };
  const tx = await getMyPendingCashPayment();
  return { phase: tx ? "pending" : "initial", tx };
}

export function useCashActivation() {
  const [state, setState] = useState({ phase: "loading", tx: null });
  const [action, setAction] = useState({ busy: false, error: null });
  const busy = useRef(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, phase: "loading" }));
    try {
      setState(await resolvePhase());
    } catch (err) {
      setState({ phase: "error", tx: null, error: friendlyDataError(err, "We couldn't check your activation.") });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn) => {
    if (busy.current) return;
    busy.current = true;
    setAction({ busy: true, error: null });
    try {
      await fn();
      setState(await resolvePhase());
      setAction({ busy: false, error: null });
    } catch (err) {
      if (/already/i.test(err?.message || "")) await load();
      setAction({ busy: false, error: cashErrorMessage(err) });
    } finally {
      busy.current = false;
    }
  };

  const request = () => run(requestCashPayment);
  const verify = (code) => run(() => verifyCashPayment(state.tx.id, code));

  return { ...state, action, load, request, verify };
}
